import { useState } from 'react';
import type { GoalRow } from '@st/contract';
import { useCreateGoal, useGoals, useUpdateGoal } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  NumberInput,
  ProgressBar,
  Select,
  SkeletonList,
  useToast,
} from '../../ui';

const TYPE_KEY: Record<GoalRow['type'], string> = {
  weight: 'goals.typeWeight',
  measurement: 'goals.typeMeasurement',
  workouts_per_week: 'goals.typeWorkoutsPerWeek',
  steps: 'goals.typeSteps',
  strength: 'goals.typeStrength',
  custom: 'goals.typeCustom',
};

const TYPES: readonly GoalRow['type'][] = [
  'weight', 'measurement', 'workouts_per_week', 'steps', 'strength', 'custom',
];

/**
 * Ciljevi s trakom napretka. `progressPct` dolazi sa servera — izvodi se iz
 * mjera, ne iz ručno upisane vrijednosti, pa se cilj i stvarnost ne razlaze.
 */
export function GoalsSection() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const goals = useGoals();
  const create = useCreateGoal();
  const update = useUpdateGoal();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<GoalRow['type']>('weight');
  const [label, setLabel] = useState('');
  const [startValue, setStartValue] = useState<number | ''>('');
  const [targetValue, setTargetValue] = useState<number | ''>('');
  const [unit, setUnit] = useState('kg');
  const [targetError, setTargetError] = useState<string | undefined>(undefined);

  async function onCreate(): Promise<void> {
    if (targetValue === '') {
      setTargetError(t('goals.targetRequired'));
      return;
    }
    try {
      await create.mutateAsync({
        type,
        targetValue,
        ...(label.trim() ? { labelHr: label.trim() } : {}),
        ...(startValue === '' ? {} : { startValue }),
        ...(unit.trim() ? { unit: unit.trim() } : {}),
      });
      setOpen(false);
      setLabel('');
      setStartValue('');
      setTargetValue('');
      setTargetError(undefined);
      toast(t('goals.created'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="eyebrow">{t('goals.title')}</h2>
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          {t('goals.create')}
        </Button>
      </div>

      <QueryBoundary
        query={goals}
        skeleton={<SkeletonList rows={2} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('goals.empty'),
          actionLabel: t('goals.create'),
          onAction: () => setOpen(true),
        }}
      >
        {(data) => (
          <ul className="flex flex-col gap-3">
            {data.items.map((goal) => (
              <li key={goal.id}>
                <Card as="article">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-[15px] text-ink">
                        {goal.label ?? t(TYPE_KEY[goal.type])}
                      </h3>
                      <p className="mt-0.5 text-[12.5px] text-ink-muted tabular-nums">
                        {goal.startValue !== null && `${t('goals.start')}: ${goal.startValue}${goal.unit ?? ''} · `}
                        {goal.currentValue !== null && `${t('goals.current')}: ${goal.currentValue}${goal.unit ?? ''} · `}
                        {t('goals.target')}: {goal.targetValue}
                        {goal.unit ?? ''}
                        {goal.dueOn && ` · ${t('goals.due')}: ${formatDate(`${goal.dueOn}T00:00:00.000Z`, locale)}`}
                      </p>
                    </div>
                    {goal.achievedAt ? (
                      <Badge tone="success">
                        {t('goals.achievedOn', { date: formatDate(goal.achievedAt, locale) })}
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={update.isPending}
                        onClick={() =>
                          update.mutate(
                            { id: goal.id, patch: { achieved: true } },
                            {
                              onSuccess: () => toast(t('goals.updated'), 'success'),
                              onError: (err) => toast(toErrorMessage(t, err).title, 'danger'),
                            },
                          )
                        }
                      >
                        {t('goals.markAchieved')}
                      </Button>
                    )}
                  </div>

                  {goal.progressPct === null ? (
                    <p className="text-[12.5px] text-ink-muted">{t('goals.noProgress')}</p>
                  ) : (
                    <ProgressBar
                      value={goal.progressPct}
                      label={t('goals.progress')}
                      valueLabel={`${goal.progressPct}%`}
                      tone={goal.achievedAt ? 'success' : 'accent'}
                    />
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </QueryBoundary>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('goals.create')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={create.isPending} onClick={() => void onCreate()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select
            id="goal-type"
            label={t('goals.type')}
            value={type}
            onChange={(e) => setType(e.target.value as GoalRow['type'])}
            options={TYPES.map((value) => ({ value, label: t(TYPE_KEY[value]) }))}
          />
          <Input
            id="goal-label"
            label={t('goals.label')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div className="grid grid-cols-3 gap-3">
            <NumberInput
              id="goal-start"
              label={t('goals.start')}
              value={startValue}
              step={0.1}
              onValueChange={setStartValue}
            />
            <NumberInput
              id="goal-target"
              label={t('goals.target')}
              value={targetValue}
              step={0.1}
              error={targetError}
              onValueChange={(v) => {
                setTargetValue(v);
                setTargetError(undefined);
              }}
            />
            <Input
              id="goal-unit"
              label={t('habits.unit')}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </section>
  );
}
